import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	stopPropagation,
	toast,
	useAlertDialog,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetType,
	OrganizationAccessLevel,
	type Payload,
	type Stored,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError } from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import { type ChangeEventHandler, memo, type ReactElement, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"

import { useFileInputRef } from "../../hooks/utils"
import { importAssetsFromCsv } from "../../imports/assets/csv"
import {
	loadGeometriesFromGeoJsonFile,
	loadGeometriesFromKmlFile,
	loadGeometriesFromShapeFile,
} from "../../mapbox/loaders"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectActiveProjectId } from "../../store/selectors"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { progressToast } from "../Toasts"

interface AssetTypeDropdownMenuProps extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	assetType: Stored<AssetType>
	children: ReactElement
}

export const AssetTypeDropdownMenu = memo((props: AssetTypeDropdownMenuProps) => {
	const { children, assetType, disabled, size, variant, radius, accentColor, ...rest } = props

	const activeProjectId = useAppSelector(selectActiveProjectId)

	if (!activeProjectId) {
		throw new Error(`Expected active projectId to exist, got ${activeProjectId}`)
	}

	const navigate = useNavigate()
	const openAlertDialog = useAlertDialog()
	const [csvFileInputRef, toggleCsvFileInput] = useFileInputRef()
	const [geoJsonFileInputRef, toggleGeoJsonFileInput] = useFileInputRef()
	const [shpFileInputRef, toggleShpFileInput] = useFileInputRef()
	const [kmlFileInputRef, toggleKmlFileInput] = useFileInputRef()

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/asset-types/$assetTypeId",
			params: (prev) => ({
				...prev,
				assetTypeId: assetType.uuid,
			}),
		})
	}, [assetType.uuid, navigate])

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/settings/asset-types/$assetTypeId",
			params: (prev) => ({
				...prev,
				assetTypeId: assetType.uuid,
			}),
		})
	}, [assetType.uuid, navigate])

	const handleConfirmCreateAssets = useCallback(
		(assetPayloads: Payload<Asset>[]) => {
			openAlertDialog({
				title: "Import Assets",
				description: `Are you sure you want to import these assets? This will create ${assetPayloads.length} ${assetType.name} assets in the project.`,
				size: "sm",
				action: (
					<>
						<LuIcon icon={modelIconMapping.import} /> Import
					</>
				),
				onAction: () => {
					const batchPromises = sdk.assets.bulkAdd(assetPayloads)

					const progressToastId = uuidv4()
					const progressToastDescription = `Importing ${assetPayloads.length} assets.`

					progressToast({
						id: progressToastId,
						description: progressToastDescription,
						progress: 0,
					})

					for (const [index, batchPromise] of batchPromises.entries()) {
						void batchPromise.then(() => {
							progressToast({
								id: progressToastId,
								description: progressToastDescription,
								progress: ((index + 1) / batchPromises.length) * 100,
							})
						})
					}

					void Promise.all(batchPromises)
						.then(() => {
							toast.success({
								title: "Success",
								description: `Successfully imported ${assetPayloads.length} assets.`,
							})
						})
						.catch((error) => {
							toast.error({
								title: "Unable to import assets",
								description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
							})
						})
						.finally(() => {
							toast.dismiss(progressToastId)
						})
				},
			})
		},
		[assetType.name, openAlertDialog],
	)

	const handleCsvFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			const { files } = event.target

			if (!files || files.length === 0) return

			const file = files[0]!

			importAssetsFromCsv(file, {
				onSuccess: (data) => {
					const payloads: Payload<Asset>[] = []

					for (const { label, latitude, longitude } of data) {
						payloads.push({
							label,
							description: null,
							geometry: turf.point([longitude, latitude]).geometry,
							status: assetType.default_status!,
							project: activeProjectId,
							asset_type: assetType.uuid,
						})
					}

					handleConfirmCreateAssets(payloads)
				},
				onError: (error) => {
					toast.error({
						title: "Unable to import assets",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				},
			})

			csvFileInputRef.current!.value = ""
		},
		[handleConfirmCreateAssets, assetType.default_status, activeProjectId, assetType.uuid, csvFileInputRef],
	)

	const handleGeoJsonFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (event) => {
			const { files } = event.target

			if (!files || files.length === 0) return

			const file = files[0]!

			try {
				const geometries = await loadGeometriesFromGeoJsonFile(file)

				const payloads: Payload<Asset>[] = []

				for (const geometry of geometries) {
					payloads.push({
						geometry: geometry,
						label: "",
						description: null,
						status: assetType.default_status!,
						project: activeProjectId,
						asset_type: assetType.uuid,
					})
				}

				handleConfirmCreateAssets(payloads)
			} catch {
				toast.error({
					title: "Failed to load .geojson file.",
					description: ERROR_MSG_UNEXPECTED,
				})
			}

			geoJsonFileInputRef.current!.value = ""
		},
		[handleConfirmCreateAssets, assetType.default_status, activeProjectId, assetType.uuid, geoJsonFileInputRef],
	)

	const handleShpFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (event) => {
			const { files } = event.target

			if (!files || files.length === 0) return

			const file = files[0]!

			try {
				const geometries = await loadGeometriesFromShapeFile(file)

				const payloads: Payload<Asset>[] = []

				for (const geometry of geometries) {
					payloads.push({
						geometry: geometry,
						label: "",
						description: null,
						status: assetType.default_status!,
						project: activeProjectId,
						asset_type: assetType.uuid,
					})
				}

				handleConfirmCreateAssets(payloads)
			} catch {
				toast.error({
					title: "Failed to load .shp file.",
					description: ERROR_MSG_UNEXPECTED,
				})
			}
			shpFileInputRef.current!.value = ""
		},
		[handleConfirmCreateAssets, assetType.default_status, activeProjectId, assetType.uuid, shpFileInputRef],
	)

	const handleKmlFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (event) => {
			const { files } = event.target

			if (!files || files.length === 0) return

			const file = files[0]!

			try {
				const geometries = await loadGeometriesFromKmlFile(file)

				const payloads: Payload<Asset>[] = []

				for (const geometry of geometries) {
					payloads.push({
						geometry: geometry,
						label: "",
						description: null,
						status: assetType.default_status!,
						project: activeProjectId,
						asset_type: assetType.uuid,
					})
				}

				handleConfirmCreateAssets(payloads)
			} catch {
				toast.error({
					title: "Failed to load .kml file.",
					description: ERROR_MSG_UNEXPECTED,
				})
			}

			kmlFileInputRef.current!.value = ""
		},
		[handleConfirmCreateAssets, assetType.default_status, activeProjectId, assetType.uuid, kmlFileInputRef],
	)

	const handleDeleteInstances = useCallback(() => {
		openAlertDialog({
			title: "Delete assets.",
			description: `Are you sure you want to delete all assets of ${assetType.name}?`,
			size: "sm",
			action: "Delete",
			onAction: () => {
				void sdk.assets.bulkDeleteAllOfType(assetType.uuid)
			},
		})
	}, [assetType.name, assetType.uuid, openAlertDialog])

	return (
		<>
			<Menu.Root {...rest}>
				<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
				<Menu.Content
					size={size}
					variant={variant}
					accentColor={accentColor}
					radius={radius}
					onClick={stopPropagation}
				>
					<Menu.Item onSelect={handleView}>
						<LuIcon icon={modelIconMapping.expand} />
						View
					</Menu.Item>
					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<Menu.Item onSelect={handleEdit}>
							<LuIcon icon={modelIconMapping.edit} />
							Edit
						</Menu.Item>
						<Menu.Separator />
						<Menu.Item onSelect={toggleCsvFileInput}>
							<LuIcon icon={modelIconMapping.upload} />
							Import CSV
						</Menu.Item>
						<Menu.Item onSelect={toggleGeoJsonFileInput}>
							<LuIcon icon={modelIconMapping.upload} />
							Import GeoJson
						</Menu.Item>
						<Menu.Item onSelect={toggleShpFileInput}>
							<LuIcon icon={modelIconMapping.upload} />
							Import Shapefile
						</Menu.Item>
						<Menu.Item onSelect={toggleKmlFileInput}>
							<LuIcon icon={modelIconMapping.upload} />
							Import KML
						</Menu.Item>
						<Menu.Separator />
						<Menu.Item onSelect={handleDeleteInstances}>
							<LuIcon icon={modelIconMapping.delete} />
							Delete from project
						</Menu.Item>
					</OrganizationAccessRequired>
				</Menu.Content>
			</Menu.Root>

			<input
				className="hidden"
				ref={csvFileInputRef}
				type="file"
				accept=".csv"
				onChange={handleCsvFileInputChange}
				onClick={stopPropagation}
			/>

			<input
				className="hidden"
				ref={geoJsonFileInputRef}
				type="file"
				accept=".geojson"
				onChange={handleGeoJsonFileInputChange}
				onClick={stopPropagation}
			/>

			<input
				className="hidden"
				ref={shpFileInputRef}
				type="file"
				accept=".zip"
				onChange={handleShpFileInputChange}
				onClick={stopPropagation}
			/>

			<input
				className="hidden"
				ref={kmlFileInputRef}
				type="file"
				accept=".kml"
				onChange={handleKmlFileInputChange}
				onClick={stopPropagation}
			/>
		</>
	)
})
AssetTypeDropdownMenu.displayName = "AssetTypeDropdownMenu"
